import { QueryClient } from '@tanstack/react-query';
import { ISupplierCalendarType } from '@bridebook/models/source/models/Suppliers/Calendars.types';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { Calendar, getCalendarsQueryKey } from '../hooks/use-calendars-query';
import {
  CalendarAvailabilityData,
  getCalendarAvailabilitySlotsQueryKey,
} from '../hooks/use-get-calendar-availability-slots-query';
import { OpenDayEventResponse, getAllOpenDaysQueryKey } from '../hooks/use-get-all-open-days-query';

/**
 * Reads showround and open day availability straight from the react query cache, without triggering
 * any request. Meant for analytics reporting from places that may or may not have already loaded the
 * supplier's calendar data.
 *
 * Resolves to `undefined` whenever the cached data isn't enough to rule availability out, so the
 * tracked property is omitted instead of being reported as a false negative.
 */
export const getViewingDateAvailability = (
  queryClient: QueryClient,
  supplierId?: string,
): boolean | undefined => {
  if (!supplierId) return undefined;

  // no cached calendars means we know nothing about this supplier's showrounds
  const calendars = queryClient.getQueryData<Calendar[]>(getCalendarsQueryKey(supplierId));
  if (!calendars) return undefined;

  const showroundCalendarId = calendars.find(
    (calendar) => calendar.type === ISupplierCalendarType.Showround,
  )?.id;
  const openDayCalendarId = calendars.find(
    (calendar) => calendar.type === ISupplierCalendarType.OpenDay,
  )?.id;

  const slots = showroundCalendarId
    ? queryClient.getQueryData<CalendarAvailabilityData>(
        getCalendarAvailabilitySlotsQueryKey(supplierId, showroundCalendarId),
      )?.slots
    : [];
  const openDays = openDayCalendarId
    ? queryClient.getQueryData<OpenDayEventResponse[]>(
        getAllOpenDaysQueryKey(supplierId, openDayCalendarId),
      )
    : [];

  // either type of date is enough, so a single cached hit already answers the question
  if (slots?.length || openDays?.length) return true;

  // without both results cached we can't tell an empty calendar from an unfetched one
  if (!slots || !openDays) return undefined;

  return false;
};

/**
 * Resolves whether a supplier had a bookable viewing date, preferring the showround and open day
 * availability already indexed on search suppliers and falling back to the cached calendar requests.
 *
 * Stays `undefined` for suppliers that carry neither indexed field and whose calendars were never
 * fetched, since we can't tell "no dates" from "never checked".
 */
export const getSupplierViewingDateAvailability = (
  queryClient: QueryClient,
  supplier: Pick<IUISupplier, 'id' | 'hasShowroundViewingDatesAvailable' | 'upcomingOpenDayDates'>,
): boolean | undefined => {
  const { hasShowroundViewingDatesAvailable, upcomingOpenDayDates } = supplier;

  if (hasShowroundViewingDatesAvailable !== undefined || upcomingOpenDayDates !== undefined) {
    return Boolean(hasShowroundViewingDatesAvailable || upcomingOpenDayDates?.length);
  }

  return getViewingDateAvailability(queryClient, supplier.id);
};
